// Template deck — Project Kickoff / Proposal.
// A self-contained, tsc-valid SlideDeck (schemaVersion 2) authored against
// slide-model.ts. Realistic sample content for one coherent fictional client
// (Octane Motors) and project (a workplace platform rollout). British English,
// no emoji, tokens-only rendering (the renderer escapes all field text).
import type { SlideDeck } from '../slide-model';

export const projectKickoffDeck: SlideDeck = {
  schemaVersion: 2,
  meta: {
    title: 'Project Kickoff',
    subtitle: 'Client proposal',
    author: 'RDx · Digital Experience',
    date: 'May 2026',
    theme: 'light',
    fontset: 'inter',
  },
  slides: [
    {
      id: 'kickoff-01',
      kind: 'cover',
      label: '01 · Cover',
      coverStyle: 'classic',
      eyebrow: 'Project kickoff',
      title: 'Octane Workplace\nPlatform Rollout',
      sub: 'Prepared for Octane Motors by RDx · Digital Experience',
      foot: 'Confidential · 2026',
      byline: '30 May 2026 · v1.0',
    },
    {
      id: 'kickoff-02',
      kind: 'split',
      label: '02 · Context',
      layout: 'image-left',
      art: 'mist',
      eyebrow: 'The context',
      title: 'Why we are here',
      lede: 'Octane Motors has consolidated six regional offices into three smart hubs, but the workplace tooling has not kept pace — staff still juggle separate systems to book a desk, sign in a visitor and report a fault.',
      items: [
        'Desk and room booking guessed, never measured across the new hubs',
        'Visitors signed in on paper, with no compliance trail',
        'Birmingham hub opens to staff in November and needs one joined-up experience',
      ],
      caption: 'One platform for every Octane building, live before the Birmingham hub opens.',
    },
    {
      id: 'kickoff-03',
      kind: 'bullets',
      label: '03 · Objectives',
      layout: 'numbered',
      eyebrow: 'Objectives',
      title: 'What success looks like',
      lede: 'The outcomes this project commits to deliver.',
      items: [
        'A single booking, visitor and issue experience across all three hubs',
        'Occupancy measured, not estimated, with a live utilisation dashboard',
        'Sixty per cent staff adoption within three months of go-live',
        'Visitor processing time cut by half versus the current paper process',
      ],
    },
    {
      id: 'kickoff-04',
      kind: 'compare',
      label: '04 · Scope',
      layout: 'two-col',
      eyebrow: 'Scope',
      title: 'What is in, what is out',
      left: {
        tag: 'In scope',
        title: 'We will deliver',
        highlight: true,
        items: [
          'Desk, room and parking booking across all three hubs',
          'Visitor management with touchless sign-in and badge printing',
          'Map-based issue reporting through to resolution',
          'Occupancy analytics and a utilisation dashboard',
        ],
      },
      right: {
        tag: 'Out of scope',
        title: 'Not this phase',
        highlight: false,
        items: [
          'Catering and meeting-room services integration',
          'Hardware procurement for room panels and sensors',
          'Workplace AI assistant — candidate for phase two',
        ],
      },
    },
    {
      id: 'kickoff-05',
      kind: 'features',
      label: '05 · Approach',
      layout: 'numbered',
      eyebrow: 'Approach',
      title: 'How we will work',
      cards: [
        {
          icon: 'home',
          h: 'Discover',
          d: 'Two weeks of workshops, a systems audit and requirements sign-off before any build begins.',
        },
        {
          icon: 'trending',
          h: 'Build',
          d: 'Iterative delivery in two-week sprints, with a working demo at the end of every sprint.',
        },
        {
          icon: 'shield',
          h: 'Land',
          d: 'User acceptance testing, floor-by-floor training and a clean handover to your facilities team.',
        },
      ],
    },
    {
      id: 'kickoff-06',
      kind: 'agenda',
      label: '06 · Team',
      layout: 'ledger',
      eyebrow: 'The team',
      title: 'Who does what',
      items: [
        { t: 'Priya Raman — Project Lead', n: 'Single point of accountability and your day-to-day contact.' },
        { t: 'James Okafor — Technical Lead', n: 'Owns architecture, integrations and delivery quality.' },
        { t: 'Hannah Webb — Service Designer', n: 'Owns the employee and visitor experience across every surface.' },
        { t: 'Mark Dennison — Octane Sponsor', n: 'Decisions, sign-off and unblocking on the client side.', active: true },
      ],
    },
    {
      id: 'kickoff-07',
      kind: 'timeline',
      label: '07 · Plan',
      layout: 'vertical',
      eyebrow: 'The plan',
      title: 'Phases and milestones',
      milestones: [
        { date: 'Weeks 1–2', t: 'Discovery', n: 'Requirements locked, success measures agreed.', active: true },
        { date: 'Weeks 3–8', t: 'Build', n: 'Iterative delivery, fortnightly demos.' },
        { date: 'Weeks 9–10', t: 'Test & train', n: 'UAT, floor training, go-live readiness.' },
        { date: 'Week 11', t: 'Go-live', n: 'Launch across all three hubs and handover to BAU.' },
      ],
    },
    {
      id: 'kickoff-08',
      kind: 'bullets',
      label: '08 · Deliverables',
      layout: 'two-col',
      eyebrow: 'Deliverables',
      title: 'What you receive',
      lede: 'The tangible artefacts at the end of each phase.',
      items: [
        'Discovery report and requirements pack',
        'Working platform delivered in four increments',
        'Test results and a formal sign-off pack',
        'Training materials and recorded sessions',
        'Operations runbook and handover documentation',
        'Thirty days of post-launch support',
      ],
    },
    {
      id: 'kickoff-09',
      kind: 'pricing',
      label: '09 · Investment',
      layout: 'spotlight',
      eyebrow: 'Investment',
      title: 'Commercials',
      lede: 'Fixed-scope, fixed-price for the phases above. Work beyond agreed scope is time-and-materials.',
      tiers: [
        {
          name: 'Octane rollout — fixed scope',
          price: '£84,000',
          period: 'total',
          note: 'Covers all phases and deliverables listed',
          tag: 'Proposed',
          featured: true,
          features: [
            'Discovery, build, test and go-live',
            'Four increments over eleven weeks',
            'Thirty days of post-launch support',
            'All deliverables and floor training',
          ],
        },
      ],
      footer: 'Payment: 30% on sign-off, 40% at build midpoint, 30% on go-live. Valid until 30 June 2026.',
    },
    {
      id: 'kickoff-10',
      kind: 'bullets',
      label: '10 · Risks',
      layout: 'list',
      eyebrow: 'Risks',
      title: 'Risks and assumptions',
      lede: 'Surfaced now so we manage them together from day one.',
      items: [
        'Assumes Octane single sign-on access is available by week two',
        'Sensor data feed from the Birmingham hub — mitigation: phased onboarding',
        'Scope change beyond agreed items handled via change control',
        'Key-person cover in place for the technical lead role',
      ],
    },
    {
      id: 'kickoff-11',
      kind: 'stat-row',
      label: '11 · Success',
      layout: 'ledger',
      eyebrow: 'Success',
      title: 'How we will know it worked',
      lede: 'The measures we review together at the thirty-day checkpoint.',
      stats: [
        { n: '60%', accent: '60%', label: 'staff adoption within three months' },
        { n: '-50%', accent: '50%', label: 'visitor processing time' },
        { n: '99.9%', label: 'platform availability against SLA' },
      ],
    },
    {
      id: 'kickoff-12',
      kind: 'cta',
      label: '12 · Close',
      layout: 'split-contact',
      eyebrow: 'Let us begin',
      headline: 'Ready to start.\nReady when you are.',
      sub: 'Sign-off this week keeps us on track for a November go-live at the Birmingham hub.',
      contact: [
        ['Project Lead', 'Priya Raman'],
        ['Email', 'priya.raman@rdx.tools'],
        ['Proposal valid until', '30 June 2026'],
      ],
    },
  ],
  notes: {
    'kickoff-01': 'Open warmly — this is a partnership, not a pitch. Confirm the room and the decision we want from them today: sign-off this week.',
    'kickoff-04': 'Be explicit on out-of-scope. The AI assistant is the obvious phase-two upsell — flag it, do not commit to it.',
    'kickoff-09': 'The £84k is fixed-scope for the eleven-week plan. Anchor on value (one platform, three hubs) before the number.',
    'kickoff-12': 'Close on the November deadline — it is their pressure, not ours, and it is what gets sign-off across the line.',
  },
};
